/**
 * Barra de ferramentas do editor de texto rico (#412, spec §4).
 *
 * Três grupos separados por divisor, na ordem em que quem redige decide: o que
 * a palavra é (texto), o que o trecho é (bloco) e a tabela. Os comandos de
 * tabela ficam sempre no mesmo lugar, desabilitados fora dela — some da barra
 * seria um botão que muda de posição debaixo do cursor.
 *
 * Linha e coluna são rótulos escritos, não ícones: o conjunto de ícones do
 * projeto não distingue "inserir linha" de "inserir coluna" sem adivinhação.
 */
import React from 'react';
import { Box, Button, Divider, IconButton, Tooltip } from '@mui/material';
import {
  FormatAlignCenter,
  FormatAlignJustify,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatUnderlined,
  GridOff,
  GridOn,
  LooksOne,
  LooksTwo,
  StrikethroughS,
} from '@mui/icons-material';
import { ChainedCommands, Editor, useEditorState } from '@tiptap/react';

interface BarraDoEditorProps {
  editor: Editor | null;
  disabled?: boolean;
}

interface EstadoDaBarra {
  negrito: boolean;
  italico: boolean;
  sublinhado: boolean;
  tachado: boolean;
  titulo1: boolean;
  titulo2: boolean;
  listaComMarcadores: boolean;
  listaNumerada: boolean;
  esquerda: boolean;
  centro: boolean;
  direita: boolean;
  justificado: boolean;
  naTabela: boolean;
  podeMesclar: boolean;
  podeDividir: boolean;
}

const NADA_ATIVO: EstadoDaBarra = {
  negrito: false,
  italico: false,
  sublinhado: false,
  tachado: false,
  titulo1: false,
  titulo2: false,
  listaComMarcadores: false,
  listaNumerada: false,
  esquerda: false,
  centro: false,
  direita: false,
  justificado: false,
  naTabela: false,
  podeMesclar: false,
  podeDividir: false,
};

interface BotaoDaBarraProps {
  rotulo: string;
  ativo?: boolean;
  desabilitado: boolean;
  aoClicar: () => void;
  children: React.ReactNode;
}

const BotaoDaBarra: React.FC<BotaoDaBarraProps> = ({
  rotulo,
  ativo,
  desabilitado,
  aoClicar,
  children,
}) => (
  <Tooltip title={rotulo}>
    <span>
      <IconButton
        size="small"
        aria-label={rotulo}
        aria-pressed={ativo === undefined ? undefined : ativo}
        color={ativo ? 'primary' : 'default'}
        disabled={desabilitado}
        onClick={aoClicar}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

export const BarraDoEditor: React.FC<BarraDoEditorProps> = ({ editor, disabled = false }) => {
  const estado =
    useEditorState({
      editor,
      selector: ({ editor: atual }): EstadoDaBarra =>
        atual
          ? {
              negrito: atual.isActive('bold'),
              italico: atual.isActive('italic'),
              sublinhado: atual.isActive('underline'),
              tachado: atual.isActive('strike'),
              titulo1: atual.isActive('heading', { level: 1 }),
              titulo2: atual.isActive('heading', { level: 2 }),
              listaComMarcadores: atual.isActive('bulletList'),
              listaNumerada: atual.isActive('orderedList'),
              esquerda: atual.isActive({ textAlign: 'left' }),
              centro: atual.isActive({ textAlign: 'center' }),
              direita: atual.isActive({ textAlign: 'right' }),
              justificado: atual.isActive({ textAlign: 'justify' }),
              naTabela: atual.isActive('table'),
              podeMesclar: atual.can().mergeCells(),
              podeDividir: atual.can().splitCell(),
            }
          : NADA_ATIVO,
    }) ?? NADA_ATIVO;

  const comando = (executar: (chain: ChainedCommands) => ChainedCommands) => () => {
    if (!editor) return;
    executar(editor.chain().focus()).run();
  };

  const inerte = disabled || !editor;
  const foraDeTabela = inerte || !estado.naTabela;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
        p: 0.5,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      <BotaoDaBarra rotulo="Negrito" ativo={estado.negrito} desabilitado={inerte} aoClicar={comando((c) => c.toggleBold())}>
        <FormatBold fontSize="small" />
      </BotaoDaBarra>
      <BotaoDaBarra rotulo="Itálico" ativo={estado.italico} desabilitado={inerte} aoClicar={comando((c) => c.toggleItalic())}>
        <FormatItalic fontSize="small" />
      </BotaoDaBarra>
      <BotaoDaBarra rotulo="Sublinhado" ativo={estado.sublinhado} desabilitado={inerte} aoClicar={comando((c) => c.toggleUnderline())}>
        <FormatUnderlined fontSize="small" />
      </BotaoDaBarra>
      <BotaoDaBarra rotulo="Tachado" ativo={estado.tachado} desabilitado={inerte} aoClicar={comando((c) => c.toggleStrike())}>
        <StrikethroughS fontSize="small" />
      </BotaoDaBarra>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <BotaoDaBarra rotulo="Título 1" ativo={estado.titulo1} desabilitado={inerte} aoClicar={comando((c) => c.toggleHeading({ level: 1 }))}>
        <LooksOne fontSize="small" />
      </BotaoDaBarra>
      <BotaoDaBarra rotulo="Título 2" ativo={estado.titulo2} desabilitado={inerte} aoClicar={comando((c) => c.toggleHeading({ level: 2 }))}>
        <LooksTwo fontSize="small" />
      </BotaoDaBarra>
      <BotaoDaBarra rotulo="Lista com marcadores" ativo={estado.listaComMarcadores} desabilitado={inerte} aoClicar={comando((c) => c.toggleBulletList())}>
        <FormatListBulleted fontSize="small" />
      </BotaoDaBarra>
      <BotaoDaBarra rotulo="Lista numerada" ativo={estado.listaNumerada} desabilitado={inerte} aoClicar={comando((c) => c.toggleOrderedList())}>
        <FormatListNumbered fontSize="small" />
      </BotaoDaBarra>
      <BotaoDaBarra rotulo="Alinhar à esquerda" ativo={estado.esquerda} desabilitado={inerte} aoClicar={comando((c) => c.setTextAlign('left'))}>
        <FormatAlignLeft fontSize="small" />
      </BotaoDaBarra>
      <BotaoDaBarra rotulo="Centralizar" ativo={estado.centro} desabilitado={inerte} aoClicar={comando((c) => c.setTextAlign('center'))}>
        <FormatAlignCenter fontSize="small" />
      </BotaoDaBarra>
      <BotaoDaBarra rotulo="Alinhar à direita" ativo={estado.direita} desabilitado={inerte} aoClicar={comando((c) => c.setTextAlign('right'))}>
        <FormatAlignRight fontSize="small" />
      </BotaoDaBarra>
      <BotaoDaBarra rotulo="Justificar" ativo={estado.justificado} desabilitado={inerte} aoClicar={comando((c) => c.setTextAlign('justify'))}>
        <FormatAlignJustify fontSize="small" />
      </BotaoDaBarra>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <BotaoDaBarra
        rotulo="Inserir tabela"
        desabilitado={inerte}
        aoClicar={comando((c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true }))}
      >
        <GridOn fontSize="small" />
      </BotaoDaBarra>
      <Button size="small" aria-label="Inserir linha" disabled={foraDeTabela} onClick={comando((c) => c.addRowAfter())}>
        + Linha
      </Button>
      <Button size="small" aria-label="Remover linha" disabled={foraDeTabela} onClick={comando((c) => c.deleteRow())}>
        − Linha
      </Button>
      <Button size="small" aria-label="Inserir coluna" disabled={foraDeTabela} onClick={comando((c) => c.addColumnAfter())}>
        + Coluna
      </Button>
      <Button size="small" aria-label="Remover coluna" disabled={foraDeTabela} onClick={comando((c) => c.deleteColumn())}>
        − Coluna
      </Button>
      <Button
        size="small"
        aria-label="Mesclar células"
        disabled={inerte || !estado.podeMesclar}
        onClick={comando((c) => c.mergeCells())}
      >
        Mesclar
      </Button>
      <Button
        size="small"
        aria-label="Dividir célula"
        disabled={inerte || !estado.podeDividir}
        onClick={comando((c) => c.splitCell())}
      >
        Dividir
      </Button>
      <BotaoDaBarra rotulo="Remover tabela" desabilitado={foraDeTabela} aoClicar={comando((c) => c.deleteTable())}>
        <GridOff fontSize="small" />
      </BotaoDaBarra>
    </Box>
  );
};

export default BarraDoEditor;
